import { useState, useMemo, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import { useAuth } from '@/context/AuthContext'
import { useTeamSlug } from '@/context/TeamSlugContext'
import { PublicAuthModal } from '@/components/public/PublicAuthModal'
import { PublicFeatureDetailModal } from '@/components/public/PublicFeatureDetailModal'
import { PublicFeatureCard } from './PublicFeatureCard'
import { Button } from '@/components/ui/button'
import { useUpvoteStatus } from '@/hooks/useUpvoteStatus'
import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { ServerCrash } from 'lucide-react'
import { useTranslation } from '@/hooks/useTranslation'

// --- Types ---
interface Tag {
	id: string
	name: string
	color?: string | null
}

interface Feature {
	id: string
	title: string
	description: string
	status: string
	upvote_count: number
	created_at: string
	tags: Tag[]
}

interface RoadmapColumn {
	id: string
	name: string
	mapped_status: string
	color?: string | null
	order_index: number
}

interface RoadmapData {
	id: string
	name: string
	slug: string
	description?: string | null
	logo_url?: string | null
	roadmap_columns: RoadmapColumn[]
	feature_requests: Feature[]
	team: {
		id: string
		name: string
		slug: string
		logo_url: string | null
	}
}

const PublicRoadmapPage = () => {
	const { t } = useTranslation()
	const { teamSlug: paramTeamSlug } = useParams<{ teamSlug: string }>()
	const { teamSlug: contextTeamSlug, isPortal } = useTeamSlug()
	const navigate = useNavigate()
	const { session } = useAuth()
	const queryClient = useQueryClient()
	const [searchParams, setSearchParams] = useSearchParams()
	const [showAuthModal, setShowAuthModal] = useState(false)
	const [selectedFeatureId, setSelectedFeatureId] = useState<string | null>(
		null
	)

	// Use context teamSlug for subdomains, fallback to URL param for backward compatibility
	const teamSlug = contextTeamSlug || paramTeamSlug

	// Handle feature URL parameter to auto-open modal
	useEffect(() => {
		const featureParam = searchParams.get('feature')

		if (featureParam && featureParam !== selectedFeatureId) {
			setSelectedFeatureId(featureParam)
		}
	}, [searchParams])

	// Fetch roadmap data
	const {
		data: roadmapData,
		isLoading,
		error,
		refetch,
	} = useQuery<RoadmapData>({
		queryKey: ['publicRoadmap', teamSlug, isPortal],
		queryFn: async (): Promise<RoadmapData> => {
			const startTime = Date.now()

			if (isPortal && contextTeamSlug) {
				const result = (await apiClient('/api/portal/roadmap', {
					needsAuth: false,
				})) as RoadmapData
				const duration = Date.now() - startTime

				return result
			} else {
				const result = (await apiClient(`/api/public/roadmaps/${teamSlug}`, {
					needsAuth: false,
				})) as RoadmapData
				const duration = Date.now() - startTime

				return result
			}
		},
		enabled: !!teamSlug,
		// Add polling to keep the public roadmap up-to-date with dashboard changes
		refetchInterval: 30 * 1000, // Refetch every 30 seconds
		refetchIntervalInBackground: true, // Keep refetching even when tab is in background
		refetchOnWindowFocus: true, // Refetch when user returns to tab
		refetchOnReconnect: true, // Refetch when network reconnects
	})

	// Log when error occurs
	if (error) {
		console.error('[ROADMAP PAGE] Failed to fetch roadmap data:', {
			error,
			teamSlug,
			isPortal,
			contextTeamSlug,
		})
	}

	// Extract all feature IDs for upvote status checking
	const featureIds = useMemo(() => {
		const ids = roadmapData?.feature_requests.map((f) => f.id) || []
		return ids
	}, [roadmapData])

	const {
		data: upvoteStatusMap = {},
		refetch: refetchUpvoteStatus,
		isLoading: isUpvoteStatusLoading,
	} = useUpvoteStatus(featureIds)

	// Upvote mutation
	const upvoteMutation = useMutation({
		mutationFn: async (featureId: string) => {
			if (isPortal && contextTeamSlug) {
				return apiClient(`/api/portal/feature/${featureId}/upvote`, {
					method: 'POST',
				})
			} else {
				return apiClient(`/api/public/features/${featureId}/upvote`, {
					method: 'POST',
				})
			}
		},
		onSuccess: (data, featureId) => {
			// Optimistically update the upvote status cache for this specific feature
			queryClient.setQueryData(
				['upvoteStatus', session?.user?.id, featureIds, isPortal],
				(oldData: Record<string, boolean> | undefined) => {
					const currentStatus = oldData?.[featureId] || false
					return {
						...oldData,
						[featureId]: !currentStatus, // Toggle the upvote status
					}
				}
			)

			// Update the roadmap data to reflect the new upvote count
			queryClient.setQueryData(
				['publicRoadmap', teamSlug, isPortal],
				(oldData: RoadmapData | undefined) => {
					if (!oldData) return oldData

					return {
						...oldData,
						feature_requests: oldData.feature_requests.map((feature) => {
							if (feature.id === featureId) {
								const currentlyUpvoted = upvoteStatusMap[featureId] || false
								const newCount = currentlyUpvoted
									? feature.upvote_count - 1
									: feature.upvote_count + 1
								return {
									...feature,
									upvote_count: newCount,
								}
							}
							return feature
						}),
					}
				}
			)
		},
		onError: (error, featureId) => {
			console.error('[ROADMAP PAGE] Upvote failed:', error)
		},
	})

	const handleUpvote = (featureId: string) => {
		if (!session?.user) {
			setShowAuthModal(true)
			return
		}

		upvoteMutation.mutate(featureId)
	}

	// Group features by column
	const columnFeatures = useMemo(() => {
		if (!roadmapData) return {}

		const grouped: Record<string, Feature[]> = {}
		roadmapData.roadmap_columns.forEach((column) => {
			grouped[column.id] = roadmapData.feature_requests.filter(
				(feature) => feature.status === column.mapped_status
			)
		})

		return grouped
	}, [roadmapData])

	if (isLoading) {
		return (
			<div className="min-h-screen bg-background font-sans">
				<div className="container mx-auto max-w-[1400px] px-4 sm:px-6 py-8 sm:py-12">
					<div className="hidden md:flex gap-6 overflow-x-hidden justify-center">
						{[...Array(3)].map((_, i) => (
							<div key={i} className="w-64 lg:w-72 flex-shrink-0 space-y-4">
								<div className="flex items-center gap-3">
									<Skeleton className="h-3 w-3 rounded-full" />
									<Skeleton className="h-5 w-24" />
								</div>
								<div className="space-y-3">
									{[...Array(3)].map((_, j) => (
										<Skeleton key={j} className="h-[92px] w-full rounded-lg" />
									))}
								</div>
							</div>
						))}
					</div>
					<div className="md:hidden space-y-6">
						{[...Array(3)].map((_, i) => (
							<div key={i} className="space-y-4">
								<div className="flex items-center gap-3">
									<Skeleton className="h-3 w-3 rounded-full" />
									<Skeleton className="h-5 w-24" />
								</div>
								<div className="space-y-3">
									{[...Array(2)].map((_, j) => (
										<Skeleton key={j} className="h-[92px] w-full rounded-lg" />
									))}
								</div>
							</div>
						))}
					</div>
				</div>
			</div>
		)
	}

	if (error || !roadmapData) {
		return (
			<div className="min-h-screen bg-background">
				<div className="container mx-auto px-6 py-8">
					<div className="flex h-96 flex-col items-center justify-center">
						<div className="text-center space-y-6 max-w-md">
							<ServerCrash className="h-16 w-16 text-muted-foreground mx-auto" />
							<div className="space-y-2">
								<h2 className="text-xl font-semibold text-foreground">
									{t('publicRoadmapPage.roadmapNotFound')}
								</h2>
								<p className="text-sm text-muted-foreground">
									{t('publicRoadmapPage.roadmapNotAvailable')}
								</p>
							</div>
							<Button variant="outline" onClick={() => navigate('/')}>
								{t('publicRoadmapPage.goHome')}
							</Button>
						</div>
					</div>
				</div>
			</div>
		)
	}

	const sortedColumns = [...roadmapData.roadmap_columns].sort(
		(a, b) => a.order_index - b.order_index
	)

	return (
		<div className="min-h-screen bg-background font-sans ">
			<main className="container mx-auto max-w-[1400px] px-4 sm:px-6 py-8 sm:py-12">
				{/* Desktop Layout */}
				<div className="hidden md:block ">
					<div
						className={cn(
							'flex gap-4 lg:gap-6 pb-4 scrollbar-hide',
							sortedColumns.length <= 4 ? 'justify-center' : 'overflow-x-auto'
						)}>
						{sortedColumns.map((column) => {
							const features = columnFeatures[column.id] || []
							return (
								<div
									key={column.id}
									className="w-64 lg:w-72 flex-shrink-0 space-y-4">
									<div className="flex items-center gap-3">
										<span
											className="portal-roadmap-column-status-dot size-[16px] rounded-full"
											style={{ backgroundColor: column.color || '#6B7280' }}
										/>
										<h2 className="label font-bold whitespace-nowrap portal-roadmap-column-title truncate text-base sm:text-lg">
											{column.name}
										</h2>
									</div>
									<div className="space-y-3">
										{features.length > 0 ? (
											features.map((feature) => (
												<PublicFeatureCard
													key={feature.id}
													feature={feature}
													hasUpvoted={upvoteStatusMap[feature.id] || false}
													isVoting={
														upvoteMutation.isPending &&
														upvoteMutation.variables === feature.id
													}
													onUpvote={handleUpvote}
													onFeatureClick={(featureId) => {
														setSelectedFeatureId(featureId)
														// Update URL to include the feature parameter
														setSearchParams({ feature: featureId })
													}}
												/>
											))
										) : (
											<div className="flex h-24 items-center justify-center rounded-lg border-2 border-dashed border-muted">
												<p className="text-xs text-muted-foreground">
													{t('publicRoadmapPage.noFeaturesYet', { columnName: column.name.toLowerCase() })}
												</p>
											</div>
										)}
									</div>
								</div>
							)
						})}
					</div>
				</div>

				{/* Mobile Layout */}
				<div className="md:hidden space-y-6 ">
					{sortedColumns.map((column) => {
						const features = columnFeatures[column.id] || []
						return (
							<div key={column.id} className="space-y-4">
								<div className="flex items-center gap-3">
									<span
										className="h-3 w-3 rounded-full"
										style={{ backgroundColor: column.color || '#6B7280' }}
									/>
									<h2 className="text-sm font-semibold text-muted-foreground">
										{column.name}
									</h2>
									<span className="text-xs text-muted-foreground ml-auto">
										{t('publicRoadmapPage.itemsCount', { count: features.length })}
									</span>
								</div>
								<div className="space-y-3">
									{features.length > 0 ? (
										features.map((feature) => (
											<PublicFeatureCard
												key={feature.id}
												feature={feature}
												hasUpvoted={upvoteStatusMap[feature.id] || false}
												isVoting={
													upvoteMutation.isPending &&
													upvoteMutation.variables === feature.id
												}
												onUpvote={handleUpvote}
												onFeatureClick={(featureId) => {
													setSelectedFeatureId(featureId)
													// Update URL to include the feature parameter
													setSearchParams({ feature: featureId })
												}}
											/>
										))
									) : (
										<div className="flex h-20 items-center justify-center rounded-lg border-2 border-dashed border-muted">
											<p className="text-xs text-muted-foreground">
												{t('publicRoadmapPage.noFeaturesYet', { columnName: column.name.toLowerCase() })}
											</p>
										</div>
									)}
								</div>
							</div>
						)
					})}
				</div>
			</main>

			<footer className="fixed bottom-0 left-0">
				<a
					href="https://feedbask.com"
					target="_blank"
					rel="noopener noreferrer">
					<Badge
						variant="outline"
						className="bg-background/80 backdrop-blur-sm hover:bg-background/90 rounded-tl-none rounded-br-none rounded-tr-sm rounded-bl-sm transition-colors">
						{t('publicRoadmapPage.poweredByFeedbask')}
						<img
							src="/favicon.ico"
							alt="Feedbask"
							className="h-4 ml-1.5 brightness-100 dark:brightness-100"
						/>
					</Badge>
				</a>
			</footer>

			<PublicAuthModal
				isOpen={showAuthModal}
				onClose={() => setShowAuthModal(false)}
				onSuccess={() => {
					setShowAuthModal(false)
					refetchUpvoteStatus()
				}}
			/>

			<PublicFeatureDetailModal
				isOpen={!!selectedFeatureId}
				onClose={() => {
					// Remove feature parameter from URL first, then clear state
					setSearchParams({})
					setSelectedFeatureId(null)
				}}
				featureId={selectedFeatureId}
				teamSlug={teamSlug!}
			/>
		</div>
	)
}

export default PublicRoadmapPage
